/**
 * Record Card-on-File Consent Actor
 *
 * After the kiosk saves a card through a SetupIntent (POS terminal or Stripe
 * Element), records the card authorization and the consent the patient saw so
 * hex-pc can treat the card as authorized (e.g. Pay Later auto-enroll).
 *
 * Throws a typed error on failure so the machine's onError fires; the machine
 * only logs it and never leaves the final screen.
 */

import { getKioskId } from "@/app/directories/kiosk/lib/kiosk-identity";
import { recordKioskCardOnFileConsent } from "@/app/directories/kiosk/server-actions/card-consent-actions";
import { datadogLogs } from "@datadog/browser-logs";
import { fromPromise } from "xstate";
import { z } from "zod";
import type { ActorMetadata } from "./create-setup-intent.actor";

/**
 * Input Schema (What this actor needs)
 */
export const RecordCardOnFileConsentInputSchema = z.object({
  consumerId: z.string().min(1),
  userId: z.string().min(1),
  orgId: z.number().int().positive(),
  setupIntentId: z.string().min(1),
});

export type RecordCardOnFileConsentInput = z.infer<
  typeof RecordCardOnFileConsentInputSchema
>;

/**
 * Success Schema (What this actor returns on success)
 */
const RecordCardOnFileConsentSuccessSchema = z.object({
  cardAuthorizationId: z.string().optional(),
  paymentAuthorizationId: z.string().optional(),
});

type RecordCardOnFileConsentSuccess = z.infer<
  typeof RecordCardOnFileConsentSuccessSchema
>;

/**
 * Error Schema (What this actor throws on failure)
 */
const RecordCardOnFileConsentErrorSchema = z.object({
  code: z.enum(["INVALID_INPUT", "BACKEND_ERROR"]),
  message: z.string(),
  retryable: z.boolean(),
});

export type RecordCardOnFileConsentError = z.infer<
  typeof RecordCardOnFileConsentErrorSchema
>;

interface RecordConsentActorWithMetadata {
  impl: ReturnType<
    typeof fromPromise<
      RecordCardOnFileConsentSuccess,
      RecordCardOnFileConsentInput
    >
  >;
  metadata: ActorMetadata;
}

const logFailure = (
  input: Partial<RecordCardOnFileConsentInput> | undefined,
  failure: RecordCardOnFileConsentError,
) => {
  datadogLogs.logger.error("[Fleet] Card-on-file consent failed", {
    action: "card_on_file_consent_failed",
    metadata: {
      kioskId: getKioskId(),
      consumerId: input?.consumerId ?? null,
      setupIntentId: input?.setupIntentId ?? null,
      errorCode: failure.code,
      errorMessage: failure.message,
    },
  });
};

const recordCardOnFileConsent = async (
  input: RecordCardOnFileConsentInput,
): Promise<RecordCardOnFileConsentSuccess> => {
  const parsed = RecordCardOnFileConsentInputSchema.safeParse(input);
  if (!parsed.success) {
    const failure: RecordCardOnFileConsentError = {
      code: "INVALID_INPUT",
      message: parsed.error.issues.map((issue) => issue.path.join(".")).join(),
      retryable: false,
    };
    logFailure(input, failure);
    throw failure;
  }
  const validated = parsed.data;

  // No client round trip before the write: the server action stamps consent
  // dates and IP itself, so a FinalScreen tap cannot cancel a pending lookup.
  const result = await recordKioskCardOnFileConsent(validated);

  if (!result.success) {
    const failure: RecordCardOnFileConsentError = {
      code: "BACKEND_ERROR",
      message: result.error,
      retryable: true,
    };
    logFailure(validated, failure);
    throw failure;
  }

  datadogLogs.logger.info("[Fleet] Card-on-file consent recorded", {
    action: "card_on_file_consent_recorded",
    metadata: {
      kioskId: getKioskId(),
      consumerId: validated.consumerId,
      setupIntentId: validated.setupIntentId,
      cardAuthorizationId: result.cardAuthorizationId ?? null,
    },
  });

  return {
    cardAuthorizationId: result.cardAuthorizationId,
    paymentAuthorizationId: result.paymentAuthorizationId,
  };
};

export const recordCardOnFileConsentActor: RecordConsentActorWithMetadata = {
  impl: fromPromise<
    RecordCardOnFileConsentSuccess,
    RecordCardOnFileConsentInput
  >(({ input }) => recordCardOnFileConsent(input)),
  metadata: {
    displayName: "recordCardOnFileConsent",
    description:
      "Record card authorization + consent for a card saved via SetupIntent",
    category: "payment",
    inputSchema: RecordCardOnFileConsentInputSchema,
    successSchema: RecordCardOnFileConsentSuccessSchema,
    errorSchema: RecordCardOnFileConsentErrorSchema,
  },
};
